import { EnrichedNotification } from '@leedpulse-monorepo/interfaces';
import { NotificationTab } from './NotificationService';

type GetRequest = (url: string, headers?: Record<string, string>) => Promise<any>;

export interface SearchContactsViewParams {
  q?: string;
  page?: number;
  hitsPerPage?: number;
  engagedFilter?: string | null;
  collaboratorId?: string | null;
  scenarioId?: string | null;
  userType?: string | null;
  userUid?: string | null;
  filter?: string | null;
  filterUnread?: boolean | string | null;
}

export interface SearchContactsViewResult {
  hits: any[];
  page: number;
  nbPages: number;
  nbHits: number;
}

const buildSearchParams = (apiKey: string, params: SearchContactsViewParams) => {
  const search = new URLSearchParams({
    q: params.q || '',
    page: String(params.page ?? 0),
    hitsPerPage: String(params.hitsPerPage ?? 20),
    apiKey,
  });

  if (params.engagedFilter) search.append('engagedFilter', params.engagedFilter);
  if (params.collaboratorId) search.append('collaboratorId', params.collaboratorId);
  if (params.scenarioId) search.append('scenarioId', params.scenarioId);
  if (params.userType) search.append('userType', params.userType);
  if (params.userUid) search.append('userUid', params.userUid);
  if (params.filter) search.append('filter', params.filter);
  if (params.filterUnread) search.append('filterUnread', 'true');

  return search;
};

// Search contacts through the backend Algolia view endpoint
export const searchContactsView = async (
  getRequest: GetRequest,
  apiKey: string,
  params: SearchContactsViewParams = {}
): Promise<SearchContactsViewResult> => {
  const search = buildSearchParams(apiKey, params);
  const json = await getRequest(`/api/v1/algolia/search/frontend/contacts/view?${search.toString()}`, {
    'x-api-key': apiKey,
  });

  const hits = (json.hits ?? []).map((hit: any) => ({
    ...hit,
    id: hit.id || hit.objectID,
    nextJobTimestamp: hit.next_first_contact_job_timestamp ?? null,
  }));

  return {
    hits,
    page: json.page ?? 0,
    nbPages: json.nbPages ?? 0,
    nbHits: json.nbHits ?? 0,
  };
};

export interface SearchNotificationsViewParams {
  q?: string;
  page?: number;
  hitsPerPage?: number;
  tab?: string | null;
  type?: string | null;
  collaboratorId?: string | null;
  userType?: string | null;
  userUid?: string | null;
  sortOrder?: string | null;
  countsOnly?: boolean;
}

// Add tabs to the hit to allow easy filtering
export type NotificationHit = EnrichedNotification & { tab: NotificationTab[] };

export interface SearchNotificationsViewResult {
  hits: NotificationHit[];
  page: number;
  nbPages: number;
  nbHits: number;
  counts: Record<string, number>; // counts is per listing tab
  feedbackCounts: Record<string, number>;
}

// Search notifications through the backend Algolia view endpoint.
// Returns the active tab page plus the per-tab counts in a single call.
export const searchNotificationsView = async (
  getRequest: GetRequest,
  apiKey: string,
  params: SearchNotificationsViewParams = {}
): Promise<SearchNotificationsViewResult> => {
  const search = new URLSearchParams({
    q: params.q || '',
    page: String(params.page ?? 0),
    hitsPerPage: String(params.hitsPerPage ?? 10),
    apiKey,
  });

  if (params.tab) search.append('tab', params.tab);
  if (params.type) search.append('type', params.type);
  if (params.collaboratorId) search.append('collaboratorId', params.collaboratorId);
  if (params.userType) search.append('userType', params.userType);
  if (params.userUid) search.append('userUid', params.userUid);
  if (params.sortOrder) search.append('sortOrder', params.sortOrder);
  if (params.countsOnly) search.append('countsOnly', 'true');

  const json = await getRequest(`/api/v1/algolia/search/frontend/notifications/view?${search.toString()}`, {
    'x-api-key': apiKey,
  });

  return {
    hits: json.hits ?? [],
    page: json.page ?? 0,
    nbPages: json.nbPages ?? 0,
    nbHits: json.nbHits ?? 0,
    counts: json.counts ?? {},
    feedbackCounts: json.feedbackCounts ?? {},
  };
};

// Count contacts per tab (one Algolia request per tab, hits not fetched)
export const countContactsViewByTabs = async (
  getRequest: GetRequest,
  apiKey: string,
  tabs: string[],
  params: SearchContactsViewParams = {}
): Promise<Record<string, number>> => {
  const results = await Promise.all(
    tabs.map(async (tab) => {
      const result = await searchContactsView(getRequest, apiKey, {
        ...params,
        engagedFilter: tab,
        hitsPerPage: 0,
        page: 0,
      });
      return [tab, result.nbHits] as const;
    })
  );

  return Object.fromEntries(results);
};

export default {
  searchContactsView,
  searchNotificationsView,
  countContactsViewByTabs,
};
